import { Button, Dropdown } from '@carbon/react';
import {
  Information,
  SortAscending,
  SortDescending,
} from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';

import { DOCUMENT_SORTS, type DocumentSort } from 'components/documents-search';
import { TooltipIcon } from 'components/tooltip-icon';
import { useDatasetAtoms, useDatasetConfig } from '../dataset-context';
import styles from './results-section.module.scss';

type Direction = 'ASC' | 'DESC';

const SORT_HELP = (
  <>
    Choose how the list is ordered.
    <br />
    <br />
    Document Date uses the first available of Effective Date → Instrument Date →
    File Date → Recorded Date. Documents with the same date keep a stable order.
    <br />
    <br />
    Use the sort direction button beside the Sort By menu to reverse the current
    order.
  </>
);

// The runsheet's sorts, in the spec's order: the document date, its title and
// type, then each of its own dates.
const SORT_ATTRIBUTES = [
  'date',
  'title',
  'document_type',
  'effective_date',
  'instrument_date',
  'file_date',
  'recorded_date',
];

const SORT_OPTIONS = SORT_ATTRIBUTES.map((attribute) =>
  DOCUMENT_SORTS.find((sort) => sort.attribute === attribute)!
);

/**
 * The direction a field starts in: the document date newest first (the
 * runsheet's reading order), every other field ascending.
 * @param {DocumentSort} option The sort field.
 * @return {Direction} The starting direction.
 */
function defaultDirection(option: DocumentSort): Direction {
  return option.attribute === 'date' ? 'DESC' : 'ASC';
}

/**
 * The direction in the field's own terms: dates run old/new, text A–Z.
 * @param {DocumentSort} option The sort field.
 * @param {Direction} direction The direction.
 * @return {string} The label, e.g. "New to Old".
 */
function directionLabel(option: DocumentSort, direction: Direction): string {
  if (option.isDate) return direction === 'ASC' ? 'Old to New' : 'New to Old';
  return direction === 'ASC' ? 'A–Z' : 'Z–A';
}

/**
 * The direction toggle names what a click does, so it reads as the other way.
 * @param {DocumentSort} option The sort field.
 * @param {Direction} direction The applied direction.
 * @return {string} The label, e.g. "Sort old to new".
 */
function toggleLabel(option: DocumentSort, direction: Direction): string {
  const inverse = direction === 'ASC' ? 'DESC' : 'ASC';
  return `Sort ${
    option.isDate
      ? directionLabel(option, inverse).toLowerCase()
      : directionLabel(option, inverse)
  }`;
}

/**
 * Sort field and direction for the dataset's card list, written to its search.
 * @return {JSX.Element} The control.
 */
export function SortBy() {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const setSearch = useSetAtom(atoms.searchAtom);
  const payload =
    useAtomValue(atoms.searchAtom).currentPayload ?? config.defaultPayload;
  const selected =
    SORT_OPTIONS.find((option) => option.attribute === payload.sortAttribute) ??
    SORT_OPTIONS[0];
  const direction: Direction =
    payload.sortDirection ?? defaultDirection(selected);

  /**
   * Applies a sort from the first page.
   * @param {string} attribute The sort attribute.
   * @param {Direction} sortDirection The direction.
   */
  const sort = (attribute: string, sortDirection: Direction) => {
    setSearch((current) => ({
      ...(current ?? config.defaultPayload),
      sortAttribute: attribute,
      sortDirection,
      page: 1,
    }));
  };

  return (
    <div className={styles.sortBy}>
      <TooltipIcon
        label="About sorting"
        definition={SORT_HELP}
        openOnClick
        align="bottom-start"
      >
        <Information />
      </TooltipIcon>
      <Dropdown
        id={`${config.id}-sort-by`}
        titleText="Sort by"
        hideLabel
        className={styles.sortField}
        label="Sort by"
        size="lg"
        items={SORT_OPTIONS}
        itemToString={(item) => item?.label ?? ''}
        renderSelectedItem={(item) => (
          <>
            <span className={styles.sortByPrefix}>Sort By:</span> {item.label} (
            {directionLabel(item, direction)})
          </>
        )}
        selectedItem={selected}
        onChange={({ selectedItem }) => {
          if (selectedItem)
            sort(selectedItem.attribute, defaultDirection(selectedItem));
        }}
      />
      <Button
        kind="ghost"
        size="lg"
        hasIconOnly
        renderIcon={direction === 'ASC' ? SortAscending : SortDescending}
        iconDescription={toggleLabel(selected, direction)}
        tooltipPosition="bottom"
        onClick={() =>
          sort(selected.attribute, direction === 'ASC' ? 'DESC' : 'ASC')
        }
      />
    </div>
  );
}
